<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required } from '@vuelidate/validators';
import router from '../../../../index';
import { isPhoneE164OrEmpty, isNumber } from 'shared/helpers/Validators';
import SalvyApi from 'dashboard/api/salvy';
import ActionCable from '@rails/actioncable';

import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    NextButton,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      inboxName: '',
      phoneNumber: '',
      apiKey: '',
      phoneNumberId: '',
      businessAccountId: '',
      showSalvyForm: false,
      salvyAreaCodes: [],
      selectedAreaCode: '',
      isProvisioningSalvy: false,
      waitingForSms: false,
      salvyCable: null,
    };
  },
  computed: {
    ...mapGetters({ uiFlags: 'inboxes/getUIFlags' }),
  },
  validations: {
    inboxName: { required },
    phoneNumber: { required, isPhoneE164OrEmpty },
    apiKey: { required },
    phoneNumberId: { required, isNumber },
    businessAccountId: { required, isNumber },
  },
  mounted() {
    this.setupActionCable();
  },
  beforeUnmount() {
    if (this.salvyCable) {
      this.salvyCable.disconnect();
    }
  },
  methods: {
    setupActionCable() {
      // Use existing ActionCable setup if available, or create a new one.
      // Assuming a standard setup where actioncable is available or we connect directly
      // In AirysChat, usually `window.chatwootPubsub` handles this, but for MVP we do a simple consumer
      // Let's use the global consumer if it exists, otherwise connect directly to /cable
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const cableUrl = `${protocol}//${window.location.host}/cable`;
      this.salvyCable = ActionCable.createConsumer(cableUrl);
      this.salvyCable.subscriptions.create('SalvySmsChannel', {
        received: data => {
          if (data.to && this.phoneNumber.includes(data.to)) {
            useAlert(`Salvy SMS Received: ${data.body}`);
            this.waitingForSms = false;
          }
        },
      });
    },
    async toggleSalvyForm() {
      this.showSalvyForm = !this.showSalvyForm;
      if (this.showSalvyForm && this.salvyAreaCodes.length === 0) {
        try {
          const response = await SalvyApi.getAreaCodes();
          if (response.data && response.data.areaCodes) {
            this.salvyAreaCodes = response.data.areaCodes
              .filter(c => c.available)
              .map(c => c.areaCode);
          } else {
            this.salvyAreaCodes = [];
          }
        } catch (error) {
          useAlert('Failed to load area codes from Salvy.');
        }
      }
    },
    async provisionSalvyNumber() {
      if (!this.selectedAreaCode) return;
      this.isProvisioningSalvy = true;
      try {
        const response = await SalvyApi.createVirtualNumber(
          this.selectedAreaCode
        );
        const newNumber =
          response.data.phoneNumber || response.data.data.phoneNumber;
        this.phoneNumber = `+${newNumber}`;
        this.showSalvyForm = false;
        this.waitingForSms = true;
        useAlert(
          'Number provisioned successfully! Waiting for SMS verification code...'
        );
      } catch (error) {
        useAlert(error.response?.data?.error || 'Failed to provision number.');
      } finally {
        this.isProvisioningSalvy = false;
      }
    },
    async createChannel() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }

      try {
        const whatsappChannel = await this.$store.dispatch(
          'inboxes/createChannel',
          {
            name: this.inboxName?.trim(),
            channel: {
              type: 'whatsapp',
              phone_number: this.phoneNumber,
              provider: 'whatsapp_cloud',
              provider_config: {
                api_key: this.apiKey,
                phone_number_id: this.phoneNumberId,
                business_account_id: this.businessAccountId,
              },
            },
          }
        );

        router.replace({
          name: 'settings_inboxes_add_agents',
          params: {
            page: 'new',
            inbox_id: whatsappChannel.id,
          },
        });
      } catch (error) {
        useAlert(
          error.message || this.$t('INBOX_MGMT.ADD.WHATSAPP.API.ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>

<template>
  <form class="flex flex-wrap flex-col mx-0" @submit.prevent="createChannel()">
    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.inboxName.$error }">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.LABEL') }}
        <input
          v-model="inboxName"
          type="text"
          :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.PLACEHOLDER')"
          @blur="v$.inboxName.$touch"
        />
        <span v-if="v$.inboxName.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.ERROR') }}
        </span>
      </label>
    </div>

    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.phoneNumber.$error }">
        <div class="flex justify-between">
          <span>{{ $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.LABEL') }}</span>
          <a
            href="#"
            class="text-sm text-n-brand underline"
            @click.prevent="toggleSalvyForm"
          >
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.SALVY.LINK_TEXT') }}
          </a>
        </div>
        <input
          v-model="phoneNumber"
          type="text"
          :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.PLACEHOLDER')"
          @blur="v$.phoneNumber.$touch"
        />
        <span v-if="v$.phoneNumber.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.ERROR') }}
        </span>
      </label>

      <!-- Salvy Form -->
      <div
        v-if="showSalvyForm"
        class="p-4 mt-2 mb-4 bg-n-alpha-1 rounded-md border border-n-weak"
      >
        <h4 class="text-sm font-semibold text-n-slate-12 mb-2">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.SALVY.FORM_TITLE') }}
        </h4>
        <p class="text-xs text-n-slate-11 mb-4">
          <strong>{{
            $t('INBOX_MGMT.ADD.WHATSAPP.SALVY.BILLING_LABEL')
          }}</strong>
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.SALVY.BILLING_DESC') }}
          <strong>{{
            $t('INBOX_MGMT.ADD.WHATSAPP.SALVY.BILLING_PRICE')
          }}</strong>
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.SALVY.BILLING_SUFFIX') }}
        </p>
        <div class="flex gap-2">
          <select
            v-model="selectedAreaCode"
            class="block w-full text-sm border border-n-weak rounded-md p-2"
          >
            <option value="" disabled>
              {{ $t('INBOX_MGMT.ADD.WHATSAPP.SALVY.AREA_CODE_PLACEHOLDER') }}
            </option>
            <option v-for="ddd in salvyAreaCodes" :key="ddd" :value="ddd">
              {{ ddd }}
            </option>
          </select>
          <NextButton
            :is-loading="isProvisioningSalvy"
            :disabled="!selectedAreaCode"
            solid
            blue
            label="Buy & Provision"
            @click.prevent="provisionSalvyNumber"
          />
        </div>
      </div>

      <div
        v-if="waitingForSms"
        class="p-3 mt-2 text-sm text-n-blue-text bg-n-blue-background rounded-md border border-n-blue-border"
      >
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.SALVY.WAITING_FOR_SMS') }}
      </div>
    </div>

    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.phoneNumberId.$error }">
        <span>
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER_ID.LABEL') }}
        </span>
        <input
          v-model="phoneNumberId"
          type="text"
          :placeholder="
            $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER_ID.PLACEHOLDER')
          "
          @blur="v$.phoneNumberId.$touch"
        />
        <span v-if="v$.phoneNumberId.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER_ID.ERROR') }}
        </span>
      </label>
    </div>

    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.businessAccountId.$error }">
        <span>
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.BUSINESS_ACCOUNT_ID.LABEL') }}
        </span>
        <input
          v-model="businessAccountId"
          type="text"
          :placeholder="
            $t('INBOX_MGMT.ADD.WHATSAPP.BUSINESS_ACCOUNT_ID.PLACEHOLDER')
          "
          @blur="v$.businessAccountId.$touch"
        />
        <span v-if="v$.businessAccountId.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.BUSINESS_ACCOUNT_ID.ERROR') }}
        </span>
      </label>
    </div>

    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.apiKey.$error }">
        <span>
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.API_KEY.LABEL') }}
        </span>
        <input
          v-model="apiKey"
          type="text"
          :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.API_KEY.PLACEHOLDER')"
          @blur="v$.apiKey.$touch"
        />
        <span v-if="v$.apiKey.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.API_KEY.ERROR') }}
        </span>
      </label>
    </div>

    <div class="w-full mt-4">
      <NextButton
        :is-loading="uiFlags.isCreating"
        type="submit"
        solid
        blue
        :label="$t('INBOX_MGMT.ADD.WHATSAPP.SUBMIT_BUTTON')"
      />
    </div>
  </form>
</template>
